import { createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { apiFetch } from '@/lib/api-fetch';
import type { VaultGetResponse } from '@syfo/types/api.client.types';

/**
 * Thunk to download vault from server
 * 
 * The vaultId parameter is the server's vault ID (syncId).
 * For locally-created vaults, pass the syncId from storage.
 * For server-downloaded vaults, the server vaultId is used directly.
 * 
 * Returns the vault data and lastModified date.
 * Note: name is no longer returned from API - caller should get from vault list.
 */
export const downloadVaultThunk = createAsyncThunk<
  { vaultId: string; data: string; lastModified?: string },
  { vaultId: string },
  { state: RootState }
>(
  'vault/downloadVault',
  async ({ vaultId }) => {
    console.log(`[Download Vault] Downloading vault ${vaultId} from server...`);
    
    const response = await apiFetch(`/api/vaults/${vaultId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      console.error(`[Download Vault] Failed for vault ${vaultId}: ${response.status}`);
      throw new Error(`Failed to download vault: ${response.status} ${response.statusText}`);
    }

    const data: VaultGetResponse = await response.json();
    const lastModified = response.headers.get('Last-Modified') || undefined;
    
    console.log(`[Download Vault] Successfully downloaded vault ${vaultId}`);
    
    return {
      vaultId: data.vaultId,
      data: data.data,
      lastModified
    };
  }
);
